import React, { useEffect, useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { MessageSquare, LogOut, Send, CheckCircle, XCircle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import api from '../../utils/api';

const AdminChats = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (!user || user.role !== 'admin') {
      navigate('/admin/login');
      return;
    }
    fetchSessions();
  }, [user, navigate]);

  useEffect(() => {
    if (selectedSession) {
      fetchMessages();
      const interval = setInterval(fetchMessages, 3000);
      return () => clearInterval(interval);
    }
  }, [selectedSession]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchSessions = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/chat/sessions');
      setSessions(response.data);
    } catch (error) {
      console.error('Error fetching sessions:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async () => {
    if (!selectedSession) return;
    try {
      const response = await api.get(`/chat/messages/${selectedSession.id}`);
      setMessages(response.data);
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedSession) return;

    const messageData = {
      session_id: selectedSession.id,
      user_id: user.id,
      user_name: 'Support Team',
      user_email: user.email,
      message: newMessage,
      is_admin: true
    };

    try {
      await api.post('/chat/message', messageData);
      setNewMessage('');
      fetchMessages();
      fetchSessions();
    } catch (error) {
      toast.error('Failed to send message');
    }
  };

  const updateSessionStatus = async (sessionId, status) => {
    try {
      await api.put(`/admin/chat/sessions/${sessionId}/status?status=${status}`);
      toast.success(`Session ${status}`);
      fetchSessions();
      if (selectedSession?.id === sessionId) {
        setSelectedSession({ ...selectedSession, status });
      }
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  if (!user || user.role !== 'admin') return null;

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-primary text-white border-b border-slate-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <h1 className="font-heading text-2xl font-bold uppercase">Admin Dashboard</h1>
          <div className="flex items-center gap-4">
            <span className="text-sm">{user.name}</span>
            <Button onClick={handleLogout} variant="outline" size="sm" className="border-white text-white hover:bg-white hover:text-primary">
              <LogOut className="h-4 w-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <nav className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-8 py-4">
            <Link to="/admin" className="text-sm font-medium text-slate-600 hover:text-secondary">
              Dashboard
            </Link>
            <Link to="/admin/products" className="text-sm font-medium text-slate-600 hover:text-secondary">
              Products
            </Link>
            <Link to="/admin/categories" className="text-sm font-medium text-slate-600 hover:text-secondary">
              Categories
            </Link>
            <Link to="/admin/orders" className="text-sm font-medium text-slate-600 hover:text-secondary">
              Orders
            </Link>
            <Link to="/admin/chats" className="text-sm font-medium text-secondary border-b-2 border-secondary pb-1">
              Customer Chats
            </Link>
            <Link to="/admin/shop-info" className="text-sm font-medium text-slate-600 hover:text-secondary">
              Shop Info
            </Link>
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center gap-3 mb-6">
          <MessageSquare className="h-8 w-8 text-secondary" />
          <h2 className="font-heading text-3xl font-bold text-primary uppercase" data-testid="chats-heading">Customer Chats</h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Chat Sessions List */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-sm shadow-sm overflow-hidden">
              <div className="p-4 bg-slate-100 border-b border-slate-200">
                <h3 className="font-heading font-bold text-primary uppercase">Chat Sessions</h3>
              </div>
              <div className="divide-y divide-slate-200 max-h-[600px] overflow-y-auto">
                {loading ? (
                  <div className="p-8 text-center text-slate-600">Loading...</div>
                ) : sessions.length === 0 ? (
                  <div className="p-8 text-center text-slate-600">No chat sessions yet</div>
                ) : (
                  sessions.map((session) => (
                    <div
                      key={session.id}
                      onClick={() => setSelectedSession(session)}
                      className={`p-4 cursor-pointer hover:bg-slate-50 transition-colors ${
                        selectedSession?.id === session.id ? 'bg-blue-50 border-l-4 border-l-secondary' : ''
                      }`}
                      data-testid={`chat-session-${session.id}`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <p className="font-medium text-primary">{session.user_name}</p>
                          {session.user_email && (
                            <p className="text-xs text-slate-500">{session.user_email}</p>
                          )}
                        </div>
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          session.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'
                        }`}>
                          {session.status}
                        </span>
                      </div>
                      <p className="text-sm text-slate-600 line-clamp-1">{session.last_message}</p>
                      <div className="flex items-center justify-between mt-2">
                        <p className="text-xs text-slate-500">
                          {new Date(session.updated_at).toLocaleString()}
                        </p>
                        <p className="text-xs text-secondary font-medium">
                          {session.message_count} messages
                        </p>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          {/* Chat Messages */}
          <div className="lg:col-span-2">
            {selectedSession ? (
              <div className="bg-white rounded-sm shadow-sm flex flex-col h-[600px]">
                <div className="p-4 bg-slate-100 border-b border-slate-200 flex items-center justify-between">
                  <div>
                    <h3 className="font-heading font-bold text-primary uppercase">{selectedSession.user_name}</h3>
                    {selectedSession.user_email && (
                      <p className="text-xs text-slate-600">{selectedSession.user_email}</p>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => updateSessionStatus(selectedSession.id, 'closed')}
                      disabled={selectedSession.status === 'closed'}
                      data-testid="close-session-button"
                    >
                      <XCircle className="h-4 w-4 mr-1" />
                      Close
                    </Button>
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50">
                  {messages.length === 0 ? (
                    <div className="text-center text-slate-500 py-8">
                      <p className="text-sm">No messages in this session yet</p>
                    </div>
                  ) : (
                    messages.map((msg) => (
                      <div
                        key={msg.id}
                        className={`flex ${msg.is_admin ? 'justify-end' : 'justify-start'}`}
                      >
                        <div
                          className={`max-w-[75%] rounded-lg p-3 ${
                            msg.is_admin
                              ? 'bg-secondary text-white'
                              : 'bg-white border border-slate-200'
                          }`}
                        >
                          {!msg.is_admin && (
                            <p className="text-xs font-medium text-primary mb-1">{msg.user_name}</p>
                          )}
                          <p className="text-sm">{msg.message}</p>
                          <p className={`text-xs mt-1 ${msg.is_admin ? 'text-white/70' : 'text-slate-500'}`}>
                            {new Date(msg.created_at).toLocaleString()}
                          </p>
                        </div>
                      </div>
                    ))
                  )}
                  <div ref={messagesEndRef} />
                </div>

                <form onSubmit={sendMessage} className="p-4 border-t border-slate-200">
                  <div className="flex gap-2">
                    <Input
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      placeholder="Type your reply..."
                      className="flex-1"
                      disabled={selectedSession.status === 'closed'}
                      data-testid="admin-message-input"
                    />
                    <Button
                      type="submit"
                      disabled={!newMessage.trim() || selectedSession.status === 'closed'}
                      data-testid="admin-send-button"
                    >
                      <Send className="h-4 w-4" />
                    </Button>
                  </div>
                </form>
              </div>
            ) : (
              <div className="bg-white rounded-sm shadow-sm h-[600px] flex items-center justify-center">
                <div className="text-center text-slate-500">
                  <MessageSquare className="h-16 w-16 mx-auto mb-4 text-slate-300" />
                  <p>Select a chat session to view messages</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminChats;
